import { nextTick } from 'vue';
import { START_LOCATION } from 'vue-router';
import { IGNORED_LEGACY_VIEWS, buildAutoloadValue, decideHistoryMode, legacyPayloadForRoute, routeLocationForLegacyEvent } from './legacyViewMap';
import { legacyNav } from '@/iframe/legacyNavState';

// Keeps Vue Router and the legacy in-app navigation (amplify 'evtChangeView') in sync, both ways:
//   legacy → route: iframeWatcher's evtChangeView subscriber calls handleLegacyViewChange()
//   route → legacy: router.afterEach publishes the matched route's legacy view
// The legacy has no portal nor login page any more (both are Vue pages): each legacy document stays blank until
// the first legacy route opens the application through the Vue session (deps.enterApplication).
// No window / document access at module scope and every side effect goes through `deps`, so the
// whole state machine runs under the node vitest environment.

export const AUTOLOAD_COOKIE = 'AUTOLOAD';
export const AUTOLOAD_SEED_KEY = 'vueAutoloadSeed';
const AUTOLOAD_MAX_AGE_S = 600;
const HOME_VIEW = 'widgets';
// Same delay the legacy uses between its greeter reset and the real view (visitPreview.js, pos.js).
const LEGACY_RESET_DELAY_MS = 2500;
const PAYLOAD_MEMORY_LIMIT = 30;
const BREAKER_WINDOW_MS = 100;
const BREAKER_MAX_PUBLISHES = 10;
const BREAKER_COOLDOWN_MS = 1000;
const IN_FLIGHT_TIMEOUT_MS = 1500;
// Same retry delay as the removed sidebar.js changeView(); ~15s, the menus load right after the first sync.
const MENU_WAIT_MS = 750;
const MENU_WAIT_MAX_ATTEMPTS = 20;
const readBrowserCookie = (name) => {
	const prefix = `${name}=`;
	const raw = document.cookie
		.split(';')
		.map((c) => c.trim())
		.find((c) => c.startsWith(prefix));
	if (!raw) return null;
	try {
		return atob(raw.substring(prefix.length)) || null;
	} catch {
		return null;
	}
};

const defaultDeps = {
	getIframeWindow: () => document.querySelector('iframe')?.contentWindow ?? null,
	unmountAll: () => {},
	log: () => {},
	now: () => Date.now(),
	isEmbedded: () => window.top !== window,
	// Same encoding and attributes as the legacy cookieHelper (bare btoa, path=/, SameSite=None; Secure).
	readCookie: readBrowserCookie,
	writeCookie: (name, value, maxAgeSeconds) => {
		document.cookie = `${name}=${btoa(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=None; Secure`;
	},
	deleteCookie: (name) => {
		document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
	},
	getSessionStorage: () => window.sessionStorage,
	schedule: (fn, ms) => setTimeout(fn, ms),
	cancel: (id) => clearTimeout(id),
	appointmentType: () => 2,
	// Opens the application in the legacy document (sessionService.enterApplication): true / a promise of it.
	enterApplication: () => false,
};

let router = null;
let deps = defaultDeps;

// Loop prevention, one mechanism per direction:
//   route → legacy → route: our publish comes straight back through iframeWatcher (amplify is
//   synchronous), but afterEach only runs once the route is committed, so the echoed view resolves
//   to the current fullPath and handleLegacyViewChange stops there.
//   legacy → route → legacy: `inFlight` marks the navigation we started so its afterEach does not
//   publish the view back to the legacy that just announced it.
let inFlight = null;
let inFlightTimer = null;

let legacyReady = false;
let pendingPayload = null;
let bootTarget = null;
let resetTimer = null;
let menuWaitTimer = null;
let lastLegacyNavAt = -Infinity;
let iframeLoadCount = 0;
let publishTimes = [];
let breakerOpenUntil = 0;
const payloadMemory = new Map();

// The current legacy document was asked to open the application (reset on every iframe load), and the URL of
// the screen it shows.
let entryRequested = false;
let lastLegacyPath = null;

const safe = (fn, fallback = null) => {
	try {
		return fn();
	} catch {
		return fallback;
	}
};

function getLegacy() {
	const win = safe(() => deps.getIframeWindow());
	const externis = safe(() => win?.externis);
	if (!externis || !safe(() => win.amplify)) return null;
	return { amplify: win.amplify, ecosVM: externis.ecosVM };
}

function clearInFlight() {
	inFlight = null;
	if (inFlightTimer) {
		deps.cancel(inFlightTimer);
		inFlightTimer = null;
	}
}

function cancelPendingReset() {
	if (resetTimer) {
		deps.cancel(resetTimer);
		resetTimer = null;
	}
	if (menuWaitTimer) {
		deps.cancel(menuWaitTimer);
		menuWaitTimer = null;
	}
}

function rememberPayload(path, evt) {
	payloadMemory.delete(path);
	payloadMemory.set(path, { appMenu: evt.appMenu, view: evt.view, label: evt.label, data: evt.data ?? null });
	if (payloadMemory.size > PAYLOAD_MEMORY_LIMIT) payloadMemory.delete(payloadMemory.keys().next().value);
}

function breakerTripped() {
	const t = deps.now();
	if (t < breakerOpenUntil) return true;

	publishTimes = publishTimes.filter((time) => t - time < BREAKER_WINDOW_MS);
	publishTimes.push(t);
	if (publishTimes.length <= BREAKER_MAX_PUBLISHES) return false;

	breakerOpenUntil = t + BREAKER_COOLDOWN_MS;
	publishTimes = [];
	deps.log('error', 'router', `evtChangeView publish storm (>${BREAKER_MAX_PUBLISHES} in ${BREAKER_WINDOW_MS}ms) — route→legacy sync paused ${BREAKER_COOLDOWN_MS}ms`);
	return true;
}

// The removed sidebar.js changeView() re-queued itself every 750ms while the payload's menu list was
// empty, and never stopped when the user simply had no entry in that menu (customers without SFA
// reach vwDocObjectives / vwWatch under menuSFA). It did nothing beyond that wait plus
// `amplify.publish('evtChangeView', …)` - the view context (ctx.app, body classes, traceView, header)
// is applied by eagle.js's own evtChangeView subscriber, whoever publishes. The bridge runs the same
// wait, bounded, sourced from Vue's own menu build (legacyNavState.js, from Core/Menus), then publishes.
const MENU_GROUPS = { menuSFA: 'sfa', menuSALES: 'sales', menuDOC: 'doc' };

function shouldWaitForMenu(payload) {
	if (payload.view === 'greeter') return false;
	const key = MENU_GROUPS[payload.appMenu];
	if (!key) return false;
	if ((legacyNav.menus[key] ?? []).length > 0) return false;
	return !legacyNav.menusReady;
}

function publish(payload, attempt = 0) {
	const legacy = getLegacy();
	if (!legacy) {
		pendingPayload = payload;
		return;
	}

	if (shouldWaitForMenu(payload) && attempt < MENU_WAIT_MAX_ATTEMPTS) {
		menuWaitTimer = deps.schedule(() => {
			menuWaitTimer = null;
			publish(payload, attempt + 1);
		}, MENU_WAIT_MS);
		return;
	}
	if (breakerTripped()) return;

	try {
		legacy.amplify.publish('evtChangeView', payload);
	} catch (error) {
		deps.log('error', 'router', `Legacy navigation to ${payload.view} failed: ${error.message}`);
	}
}

function requestLegacyNavigation(payload) {
	if (!payload) return;
	cancelPendingReset();

	if (!legacyReady || !getLegacy()) {
		pendingPayload = payload;
		return;
	}
	pendingPayload = null;

	// The legacy only rebinds a component when currentView changes, so reaching the same view with
	// other data (/visit/A → /visit/B) goes through the same greeter reset the legacy uses itself.
	const currentView = safe(() => getLegacy()?.ecosVM?.currentView?.());
	if (currentView === payload.view) {
		publish({ view: 'greeter' });
		resetTimer = deps.schedule(() => {
			resetTimer = null;
			publish(payload);
		}, LEGACY_RESET_DELAY_MS);
		return;
	}

	publish(payload);
}

function payloadForRoute(route) {
	return legacyPayloadForRoute(route, payloadMemory.get(route.fullPath), { appointmentType: safe(() => deps.appointmentType()) });
}

const onLegacyRoute = () => Boolean(router.currentRoute.value.meta?.legacyView);

// The first legacy route of a document opens the application, carrying that route as the AUTOLOAD deep link.
function requestEntry(route) {
	if (entryRequested || !route?.meta?.legacyView) return false;
	entryRequested = true;
	seedLegacyBootNavigation(route.fullPath);
	Promise.resolve(safe(() => deps.enterApplication(), false))
		.then((entered) => {
			if (!entered) entryRequested = false;
		})
		.catch(() => {
			entryRequested = false;
		});
	return true;
}

// The portal is a Vue page: the legacy asks for it (c_reloadWebApp) and the chrome's portal button too.
export function navigateToPortal() {
	if (!router) return false;
	router.push({ name: 'Portal' }).catch(() => {});
	return true;
}

// Called by iframeWatcher once a new legacy document is attached: shown on a legacy route, it opens the application.
export function requestApplicationEntry() {
	if (!router) return false;
	return requestEntry(router.currentRoute.value);
}

async function onAfterEach(to, from, failure) {
	if (failure) return;

	if (inFlight && inFlight.path === to.fullPath) {
		clearInFlight();
		return;
	}

	if (!to.meta?.legacyView) {
		// Native Vue page: nothing is published, so legacy zones would otherwise keep running behind the hidden iframe.
		// The portal keeps them: the application waits behind it, as it was, for the way back.
		if (legacyReady && !to.meta?.portal) safe(() => deps.unmountAll());
		return;
	}

	// Entering the application: the legacy boots its own first view; a deep link reaches it through the
	// AUTOLOAD cookie written by seedLegacyBootNavigation(), never through an early publish.
	if (requestEntry(to) || from === START_LOCATION) return;
	// Back from the portal to the screen the application still shows.
	if (legacyReady && !from.meta?.legacyView && to.fullPath === lastLegacyPath) return;

	await nextTick();
	requestLegacyNavigation(payloadForRoute(to));
}

function readSession(key) {
	return safe(() => deps.getSessionStorage()?.getItem(key));
}

function releaseOwnAutoload() {
	const own = readSession(AUTOLOAD_SEED_KEY);
	if (!own) return;
	safe(() => deps.getSessionStorage()?.removeItem(AUTOLOAD_SEED_KEY));
	if (safe(() => deps.readCookie(AUTOLOAD_COOKIE)) === own) safe(() => deps.deleteCookie(AUTOLOAD_COOKIE));
}

// Entry point of the Vue chrome menus. The URL still drives the legacy: the menu payload (appMenu,
// label, data) is recorded for the target URL, and afterEach publishes it once the route is committed.
export function navigateToLegacyView(payload) {
	if (!router || !payload?.view) return false;

	const location = routeLocationForLegacyEvent(payload);
	if (!location) {
		deps.log('warn', 'router', `No route for legacy view "${payload.view}" — published without a URL change`);
		requestLegacyNavigation(payload);
		return false;
	}

	const target = router.resolve(location);
	rememberPayload(target.fullPath, payload);

	if (target.fullPath === router.currentRoute.value.fullPath) {
		// Like the legacy menus, re-selecting the displayed view does nothing.
		if (safe(() => getLegacy()?.ecosVM?.currentView?.()) !== payload.view) requestLegacyNavigation(payloadForRoute(target));
		return true;
	}

	router.push(target.fullPath).catch(() => {});
	return true;
}

export function installLegacyNavigationBridge(appRouter, overrides = {}) {
	router = appRouter;
	deps = { ...defaultDeps, ...overrides };
	router.afterEach(onAfterEach);
}

// Called right before a legacy document opens the application. The legacy lands on its home view unless an
// AUTOLOAD cookie is present (eagle.js evtAppSwitching / replayAutoload, formerly sidebar.js), the path an SSO
// module link takes too. It stays legacy-driven on purpose: the boot evtAppSwitching can fire before this
// window has subscribed to the freshly loaded document's amplify.
export function seedLegacyBootNavigation(path) {
	if (!router) return false;

	releaseOwnAutoload();
	bootTarget = null;

	if (safe(() => deps.isEmbedded(), false)) return false;

	const target = router.resolve(path);
	const value = buildAutoloadValue(target);
	if (!value) return false;

	// A deep link written by the legacy app or the BO wins over the current URL.
	if (safe(() => deps.readCookie(AUTOLOAD_COOKIE))) return false;

	safe(() => deps.writeCookie(AUTOLOAD_COOKIE, value, AUTOLOAD_MAX_AGE_S));
	safe(() => deps.getSessionStorage()?.setItem(AUTOLOAD_SEED_KEY, value));
	bootTarget = target;
	return true;
}

// js/Main.js protectMobilePreventBack(), called by context.js setHashSilently() on every legacy
// view change, pushes a history entry and sets onpopstate = () => history.go(1): Back is cancelled
// for the whole browsing context (the iframe shares session history with this window).
export function neutralizeLegacyHistoryTrap(win) {
	if (!win) return false;
	try {
		win.protectMobilePreventBack = () => {};
		win.onpopstate = null;
		return true;
	} catch (error) {
		deps.log('warn', 'router', `Could not neutralize the legacy Back trap: ${error.message}`);
		return false;
	}
}

// Called from iframeWatcher's iframe.onload, before anything else: each load is a fresh legacy document.
export function onLegacyIframeLoad(win) {
	iframeLoadCount += 1;
	neutralizeLegacyHistoryTrap(win);

	legacyReady = false;
	lastLegacyNavAt = -Infinity;
	cancelPendingReset();
	entryRequested = false;
	lastLegacyPath = null;
	// Recorded payloads hold live objects of the previous document.
	payloadMemory.clear();
}

export function handleLegacyViewChange(evt) {
	if (!router) return;

	const view = evt?.view;
	if (!view || IGNORED_LEGACY_VIEWS.has(view)) return;

	const firstCommit = !legacyReady;
	legacyReady = true;

	if (firstCommit && pendingPayload) {
		// Vue asked for a view before the legacy was ready: that newer intent wins over the boot view.
		const payload = pendingPayload;
		pendingPayload = null;
		bootTarget = null;
		deps.schedule(() => requestLegacyNavigation(payload), 0);
		return;
	}

	if (firstCommit && bootTarget) {
		const target = bootTarget;
		bootTarget = null;
		releaseOwnAutoload();
		if (view !== target.meta.legacyView && view === HOME_VIEW) {
			// AUTOLOAD was not honoured and the legacy fell back to its home: replay the deep link once,
			// after the current evtChangeView dispatch has finished. Any other view is a deliberate
			// legacy redirect (e.g. pending offline orders) and the URL follows it below.
			deps.schedule(() => requestLegacyNavigation(payloadForRoute(target)), 0);
			return;
		}
	}

	const location = routeLocationForLegacyEvent(evt);
	if (!location) {
		deps.log('warn', 'router', `No route for legacy view "${view}" — URL left unchanged`);
		return;
	}

	const target = router.resolve(location);
	rememberPayload(target.fullPath, evt);
	lastLegacyPath = target.fullPath;

	// The application keeps running hidden behind the portal: its own navigations do not take the user away from it.
	if (!onLegacyRoute()) return;

	// Also stamped for our own echoed publishes: a legacy redirect fired right after a Vue-initiated
	// navigation (the target view bouncing elsewhere) then replaces that entry instead of stacking one
	// that Back would bounce off again.
	const now = deps.now();
	const sinceLastLegacyNav = now - lastLegacyNavAt;
	lastLegacyNavAt = now;

	if (target.fullPath === router.currentRoute.value.fullPath) return;

	const mode = decideHistoryMode({
		embedded: safe(() => deps.isEmbedded(), false),
		firstCommit,
		state: evt.state ?? null,
		routeHistoryMode: target.meta?.historyMode ?? null,
		sinceLastLegacyNav,
		previousPath: safe(() => router.options.history.state?.back),
		targetPath: target.fullPath,
	});

	clearInFlight();
	inFlight = { path: target.fullPath };

	if (mode === 'back') {
		router.back();
		inFlightTimer = deps.schedule(clearInFlight, IN_FLIGHT_TIMEOUT_MS);
		return;
	}

	router[mode](target.fullPath)
		.catch(() => {})
		.finally(() => {
			if (inFlight?.path === target.fullPath) clearInFlight();
		});
}

export function __resetLegacyNavigationBridgeForTests() {
	router = null;
	deps = defaultDeps;
	inFlight = null;
	inFlightTimer = null;
	legacyReady = false;
	pendingPayload = null;
	bootTarget = null;
	resetTimer = null;
	menuWaitTimer = null;
	lastLegacyNavAt = -Infinity;
	iframeLoadCount = 0;
	publishTimes = [];
	breakerOpenUntil = 0;
	payloadMemory.clear();
	entryRequested = false;
	lastLegacyPath = null;
}
